/**
 * TreeGrid Example Page
 *
 * Demonstrates all TreeGrid features with mock data:
 * - Project Items configuration
 * - Transaction configuration
 * - Inline editing
 * - Drag & drop (within grid and cross-grid)
 * - Selection
 * - Keyboard navigation
 * - Cross-grid drag and drop
 */

import React, { useState, useMemo, useCallback } from "react";
import {
  TreeGridComponent,
  FooterAction,
} from "../../components/ui/TreeGridComponent/TreeGridComponent";
import {
  TreeGridColumn,
  TreeGridRow,
  ComboBoxItem,
} from "../../components/ui/TreeGridComponent/types";
import { ToastQueue } from '@adobe/react-spectrum';
import { Provider, defaultTheme } from "@adobe/react-spectrum";

// Mock Product Catalog for ComboBox search
const PRODUCT_CATALOG: ComboBoxItem[] = [
  {
    id: "SVR-001",
    label: "Main Server System",
    sku: "SVR-001",
    unitPrice: 15000.0,
    catalogPrice: 18000.0,
    category: "Servers",
  },
  {
    id: "SVR-002",
    label: "Backup Server",
    sku: "SVR-002",
    unitPrice: 12000.0,
    catalogPrice: 14500.0,
    category: "Servers",
  },
  {
    id: "CPU-X99",
    label: "CPU Module",
    sku: "CPU-X99",
    unitPrice: 2500.0,
    catalogPrice: 3000.0,
    category: "Components",
  },
  {
    id: "CPU-X100",
    label: "CPU Module Pro",
    sku: "CPU-X100",
    unitPrice: 3500.0,
    catalogPrice: 4200.0,
    category: "Components",
  },
  {
    id: "MEM-64",
    label: "Memory Module 64GB",
    sku: "MEM-64",
    unitPrice: 500.0,
    catalogPrice: 600.0,
    category: "Memory",
  },
  {
    id: "MEM-128",
    label: "Memory Module 128GB",
    sku: "MEM-128",
    unitPrice: 900.0,
    catalogPrice: 1100.0,
    category: "Memory",
  },
  {
    id: "NET-001",
    label: "Network Equipment",
    sku: "NET-001",
    unitPrice: 8000.0,
    catalogPrice: 10000.0,
    category: "Network",
  },
  {
    id: "SW-48P",
    label: "Switch 48-Port",
    sku: "SW-48P",
    unitPrice: 1200.0,
    catalogPrice: 1500.0,
    category: "Network",
  },
  {
    id: "SW-24P",
    label: "Switch 24-Port",
    sku: "SW-24P",
    unitPrice: 800.0,
    catalogPrice: 1000.0,
    category: "Network",
  },
  {
    id: "SSD-2TB",
    label: "External SSD 2TB",
    sku: "SSD-2TB",
    unitPrice: 250.0,
    catalogPrice: 300.0,
    category: "Storage",
  },
  {
    id: "SSD-4TB",
    label: "External SSD 4TB",
    sku: "SSD-4TB",
    unitPrice: 450.0,
    catalogPrice: 550.0,
    category: "Storage",
  },
  {
    id: "USB-HUB-C",
    label: "USB-C Hub",
    sku: "USB-HUB-C",
    unitPrice: 80.0,
    catalogPrice: 100.0,
    category: "Accessories",
  },
  {
    id: "MOUSE-W",
    label: "Wireless Mouse",
    sku: "MOUSE-W",
    unitPrice: 45.0,
    catalogPrice: 60.0,
    category: "Accessories",
  },
  {
    id: "KB-W",
    label: "Wireless Keyboard",
    sku: "KB-W",
    unitPrice: 75.0,
    catalogPrice: 95.0,
    category: "Accessories",
  },
  {
    id: "MON-27-4K",
    label: 'Monitor 27" 4K',
    sku: "MON-27-4K",
    unitPrice: 450.0,
    catalogPrice: 550.0,
    category: "Monitors",
  },
  {
    id: "MON-32-4K",
    label: 'Monitor 32" 4K',
    sku: "MON-32-4K",
    unitPrice: 650.0,
    catalogPrice: 800.0,
    category: "Monitors",
  },
  {
    id: "CAM-HD",
    label: "Webcam HD",
    sku: "CAM-HD",
    unitPrice: 120.0,
    catalogPrice: 150.0,
    category: "Accessories",
  },
  {
    id: "CAM-4K",
    label: "Webcam 4K Pro",
    sku: "CAM-4K",
    unitPrice: 220.0,
    catalogPrice: 280.0,
    category: "Accessories",
  },
  {
    id: "UPS-1500",
    label: "UPS 1500VA",
    sku: "UPS-1500",
    unitPrice: 350.0,
    catalogPrice: 420.0,
    category: "Power",
  },
  {
    id: "UPS-3000",
    label: "UPS 3000VA",
    sku: "UPS-3000",
    unitPrice: 650.0,
    catalogPrice: 780.0,
    category: "Power",
  },
];

// Simulated async product search function
const searchProducts = async (filterText: string): Promise<ComboBoxItem[]> => {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 300));

  const searchLower = filterText.toLowerCase();

  // Search by name, SKU, or category
  return PRODUCT_CATALOG.filter(
    (product) =>
      product.label.toLowerCase().includes(searchLower) ||
      product.sku.toLowerCase().includes(searchLower) ||
      product.category?.toLowerCase().includes(searchLower)
  );
};

// Mock data for Project Items
const generateProjectItemsData = (): TreeGridRow[] => {
  return [
    {
      id: "item-1",
      parentId: null,
      depth: 0,
      childrenIds: ["item-2", "item-3"],
      isExpanded: true,
      rowType: "product",
      sortOrder: 0,
      data: {
        product: "Main Server System",
        sku: "SVR-001",
        quantity: 1,
        unitPrice: 15000.0,
        catalogPrice: 18000.0,
        total: 15000.0,
        serialNumber: "SN-2024-001",
      },
    },
    {
      id: "item-2",
      parentId: "item-1",
      depth: 1,
      childrenIds: [],
      isExpanded: false,
      rowType: "product",
      sortOrder: 0,
      data: {
        product: "CPU Module",
        sku: "CPU-X99",
        quantity: 2,
        unitPrice: 2500.0,
        catalogPrice: 3000.0,
        total: 5000.0,
        serialNumber: "SN-2024-002",
      },
    },
    {
      id: "item-3",
      parentId: "item-1",
      depth: 1,
      childrenIds: [],
      isExpanded: false,
      rowType: "product",
      sortOrder: 1,
      data: {
        product: "Memory Module 64GB",
        sku: "MEM-64",
        quantity: 4,
        unitPrice: 500.0,
        catalogPrice: 600.0,
        total: 2000.0,
        serialNumber: "SN-2024-003",
      },
    },
    {
      id: "item-4",
      parentId: null,
      depth: 0,
      childrenIds: ["item-5"],
      isExpanded: true,
      rowType: "product",
      sortOrder: 1,
      data: {
        product: "Network Equipment",
        sku: "NET-001",
        quantity: 1,
        unitPrice: 8000.0,
        catalogPrice: 10000.0,
        total: 8000.0,
        serialNumber: "SN-2024-004",
      },
    },
    {
      id: "item-5",
      parentId: "item-4",
      depth: 1,
      childrenIds: [],
      isExpanded: false,
      rowType: "product",
      sortOrder: 0,
      data: {
        product: "Switch 48-Port",
        sku: "SW-48P",
        quantity: 2,
        unitPrice: 1200.0,
        catalogPrice: 1500.0,
        total: 2400.0,
        serialNumber: "SN-2024-005",
      },
    },
  ];
};

// Mock data for Transactions
const generateTransactionData = (): TreeGridRow[] => {
  return [
    {
      id: "txn-1",
      parentId: null,
      depth: 0,
      childrenIds: [],
      isExpanded: false,
      rowType: "simple",
      sortOrder: 0,
      data: {
        description: "Professional Services - System Integration",
        quantity: 40,
        unitPrice: 150.0,
        discount: 10,
        total: 5400.0,
        notes: "Includes configuration and testing",
      },
    },
    {
      id: "txn-2",
      parentId: null,
      depth: 0,
      childrenIds: ["txn-3", "txn-4"],
      isExpanded: true,
      rowType: "product",
      sortOrder: 1,
      data: {
        product: "Software Licenses",
        description: "Enterprise license bundle",
        quantity: 50,
        unitPrice: 200.0,
        discount: 15,
        total: 8500.0,
        notes: "Annual subscription",
      },
    },
    {
      id: "txn-3",
      parentId: "txn-2",
      depth: 1,
      childrenIds: [],
      isExpanded: false,
      rowType: "simple",
      sortOrder: 0,
      data: {
        description: "Operating System License",
        quantity: 50,
        unitPrice: 100.0,
        discount: 15,
        total: 4250.0,
        notes: "",
      },
    },
    {
      id: "txn-4",
      parentId: "txn-2",
      depth: 1,
      childrenIds: [],
      isExpanded: false,
      rowType: "simple",
      sortOrder: 1,
      data: {
        description: "Database License",
        quantity: 50,
        unitPrice: 100.0,
        discount: 15,
        total: 4250.0,
        notes: "",
      },
    },
  ];
};

// Mock data for Available Products (for cross-grid drag demo)
const generateAvailableProductsData = (): TreeGridRow[] => {
  return [
    {
      id: "avail-1",
      parentId: null,
      depth: 0,
      childrenIds: [],
      isExpanded: false,
      rowType: "product",
      sortOrder: 0,
      data: {
        product: "External SSD 2TB",
        sku: "SSD-2TB",
        quantity: 1,
        unitPrice: 250.0,
        catalogPrice: 300.0,
        total: 250.0,
        serialNumber: "",
      },
    },
    {
      id: "avail-2",
      parentId: null,
      depth: 0,
      childrenIds: [],
      isExpanded: false,
      rowType: "product",
      sortOrder: 1,
      data: {
        product: "USB-C Hub",
        sku: "USB-HUB-C",
        quantity: 1,
        unitPrice: 80.0,
        catalogPrice: 100.0,
        total: 80.0,
        serialNumber: "",
      },
    },
    {
      id: "avail-3",
      parentId: null,
      depth: 0,
      childrenIds: [],
      isExpanded: false,
      rowType: "product",
      sortOrder: 2,
      data: {
        product: "Wireless Mouse",
        sku: "MOUSE-W",
        quantity: 1,
        unitPrice: 45.0,
        catalogPrice: 60.0,
        total: 45.0,
        serialNumber: "",
      },
    },
    {
      id: "avail-4",
      parentId: null,
      depth: 0,
      childrenIds: [],
      isExpanded: false,
      rowType: "product",
      sortOrder: 3,
      data: {
        product: 'Monitor 27" 4K',
        sku: "MON-27-4K",
        quantity: 1,
        unitPrice: 450.0,
        catalogPrice: 550.0,
        total: 450.0,
        serialNumber: "",
      },
    },
    {
      id: "avail-5",
      parentId: null,
      depth: 0,
      childrenIds: [],
      isExpanded: false,
      rowType: "product",
      sortOrder: 4,
      data: {
        product: "Webcam HD",
        sku: "CAM-HD",
        quantity: 1,
        unitPrice: 120.0,
        catalogPrice: 150.0,
        total: 120.0,
        serialNumber: "",
      },
    },
  ];
};

export default function TreeGridExample() {
  const [activeTab, setActiveTab] = useState<
    "project-items" | "transactions" | "cross-grid"
  >("project-items");
  const [projectItemsData, setProjectItemsData] = useState(
    generateProjectItemsData()
  );
  const [transactionData, setTransactionData] = useState(
    generateTransactionData()
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // State for cross-grid demo
  const [availableProductsData, setAvailableProductsData] = useState(
    generateAvailableProductsData()
  );
  const [selectedProductsData, setSelectedProductsData] = useState<
    TreeGridRow[]
  >([]);

  // Project Items Columns
  const projectItemsColumns: TreeGridColumn[] = useMemo(
    () => [
      {
        key: "product",
        header: "Product Name",
        width: 350,
        editable: true,
        editorType: "combobox",
        isTreeColumn: true,
        showFolderIcon: true,
        comboBoxConfig: {
          placeholder: "Search products...",
          menuWidth: 450,
          debounceMs: 200,

          // Async load function - searches products
          loadItems: searchProducts,

          // Group results by category
          groupBy: (item) => item.category || "Other",

          // What to show in the input after selection
          getDisplayValue: (item) => item.label,

          // What value to store in the cell
          getValue: (item) => item.label,

          // Custom render for dropdown items
          renderItem: (item) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: "16px",
                padding: "2px 0",
              }}
            >
              <span
                style={{
                  fontWeight: 500,
                  color: "#1a1a1a",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                }}
              >
                {item.label}
              </span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontFamily: "monospace",
                    background: "#f0f0f0",
                    padding: "2px 6px",
                    borderRadius: "3px",
                    fontSize: "11px",
                    color: "#555",
                  }}
                >
                  {item.sku}
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    color: "#2e7d32",
                    fontSize: "13px",
                  }}
                >
                  ${item.unitPrice.toLocaleString()}
                </span>
              </div>
            </div>
          ),

          // Update multiple fields when a product is selected
          onSelectionChange: (item, row) => {
            if (!item) return {};
            return {
              product: item.label,
              sku: item.sku,
              unitPrice: item.unitPrice,
              catalogPrice: item.catalogPrice,
              total: (row.data.quantity || 1) * item.unitPrice,
            };
          },
        },
      },
      {
        key: "sku",
        header: "SKU",
        width: 120,
        editable: false,
        editorType: "text",
      },
      {
        key: "quantity",
        header: "Quantity",
        width: 100,
        editable: true,
        editorType: "number",
        align: "right",
        numberFormat: {
          decimals: 2,
          thousandsSeparator: false,
        },
        validate: (value: any) => {
          const num = parseFloat(value);
          if (isNaN(num) || num <= 0) return "Quantity must be greater than 0";
          return null;
        },
      },
      {
        key: "unitPrice",
        header: "Unit Price",
        width: 120,
        editable: true,
        editorType: "number",
        align: "right",
        numberFormat: {
          decimals: 2,
          thousandsSeparator: true,
          prefix: "$",
        },
        format: (value: any) => `$${parseFloat(value || 0).toFixed(2)}`,
      },
      {
        key: "catalogPrice",
        header: "Catalog Price",
        width: 130,
        editable: false,
        editorType: "number",
        align: "right",
        format: (value: any) => `$${parseFloat(value || 0).toFixed(2)}`,
      },
      {
        key: "total",
        header: "Total",
        width: 120,
        editable: false,
        align: "right",
        showTotal: true,
        totalFormat: (total: number) => `$${total.toFixed(2)}`,
        format: (value: any) => `$${parseFloat(value || 0).toFixed(2)}`,
        render: (value: any, row: TreeGridRow) => {
          const total = row.data.quantity * row.data.unitPrice;
          return <strong>${total.toFixed(2)}</strong>;
        },
      },
      {
        key: "serialNumber",
        header: "Serial Number",
        width: 150,
        editable: true,
        editorType: "text",
      },
    ],
    []
  );

  // Transaction Columns
  const transactionColumns: TreeGridColumn[] = useMemo(
    () => [
      {
        key: "description",
        header: "Description",
        width: 350,
        editable: true,
        editorType: "text",
        isTreeColumn: true,
      },
      {
        key: "quantity",
        header: "Quantity",
        width: 100,
        editable: true,
        editorType: "number",
        align: "right",
        numberFormat: {
          decimals: 2,
        },
      },
      {
        key: "unitPrice",
        header: "Unit Price",
        width: 120,
        editable: true,
        editorType: "number",
        align: "right",
        format: (value: any) => `$${parseFloat(value || 0).toFixed(2)}`,
        onCommit: (value: any, row: TreeGridRow) => {
          // Recalculate total when unit price changes
          row.data.total =
            row.data.quantity * value * (1 - row.data.discount / 100);
        },
      },
      {
        key: "discount",
        header: "Discount %",
        width: 110,
        editable: true,
        editorType: "number",
        align: "right",
        numberFormat: {
          decimals: 2,
          suffix: "%",
        },
        format: (value: any) => `${parseFloat(value || 0).toFixed(2)}%`,
        validate: (value: any) => {
          const num = parseFloat(value);
          if (isNaN(num) || num < 0 || num > 100) {
            return "Discount must be between 0 and 100";
          }
          return null;
        },
        onCommit: (value: any, row: TreeGridRow) => {
          // Recalculate unit price when discount changes
          if (row.data.catalogPrice) {
            row.data.unitPrice = row.data.catalogPrice * (1 - value / 100);
            row.data.total = row.data.quantity * row.data.unitPrice;
          }
        },
      },
      {
        key: "total",
        header: "Total",
        width: 120,
        editable: false,
        align: "right",
        render: (value: any, row: TreeGridRow) => {
          const total =
            row.data.quantity *
            row.data.unitPrice *
            (1 - (row.data.discount || 0) / 100);
          return <strong>${total.toFixed(2)}</strong>;
        },
        showTotal: true,
        footer: (rows: TreeGridRow[]) => {
          const total = rows.reduce((sum, row) => {
            return (
              sum +
              row.data.quantity *
                row.data.unitPrice *
                (1 - (row.data.discount || 0) / 100)
            );
          }, 0);
          return <strong>${total.toFixed(2)}</strong>;
        },
      },
      {
        key: "notes",
        header: "Notes",
        width: 200,
        editable: true,
        editorType: "text",
      },
    ],
    []
  );

  // Callbacks
  const handleRowUpdate = async (row: TreeGridRow) => {
    console.log("handleRowUpdate called:", row);
    console.log("handleRowUpdate row.data:", row.data);
    setIsLoading(true);

    // Simulate API call with delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    console.log("Updating data after delay...");
    if (activeTab === "project-items") {
      setProjectItemsData((prev) =>
        prev.map((r) => (r.id === row.id ? row : r))
      );
    } else {
      setTransactionData((prev) =>
        prev.map((r) => (r.id === row.id ? row : r))
      );
    }

    setIsLoading(false);
    console.log("Data updated, showing toast");
    ToastQueue.positive("Record updated successfully", { timeout: 3000 });
  };

  const handleRowCreate = (
    parentId: string | null,
    rowType: "simple" | "product"
  ) => {
    console.log("Create row:", { parentId, rowType });
    const newRow: TreeGridRow = {
      id: `new-${Date.now()}`,
      parentId,
      depth: parentId ? 1 : 0,
      childrenIds: [],
      isExpanded: false,
      rowType,
      sortOrder: 0,
      data:
        activeTab === "project-items"
          ? {
              product: "New Product",
              sku: "",
              quantity: 1,
              unitPrice: 0,
              catalogPrice: 0,
              total: 0,
              serialNumber: "",
            }
          : {
              description: "New Item",
              quantity: 1,
              unitPrice: 0,
              discount: 0,
              total: 0,
              notes: "",
            },
    };

    if (activeTab === "project-items") {
      setProjectItemsData((prev) => [...prev, newRow]);
    } else {
      setTransactionData((prev) => [...prev, newRow]);
    }
  };

  const handleRowDelete = (rowId: string) => {
    console.log("Delete row:", rowId);
    if (activeTab === "project-items") {
      setProjectItemsData((prev) =>
        prev.filter((r) => r.id !== rowId && r.parentId !== rowId)
      );
    } else {
      setTransactionData((prev) =>
        prev.filter((r) => r.id !== rowId && r.parentId !== rowId)
      );
    }
  };

  const handleRowMove = (
    rowId: string,
    newParentId: string | null,
    index: number
  ) => {
    console.log("Move row:", { rowId, newParentId, index });
    // In a real app, you would update the backend and refresh data
  };

  const handleSelectionChange = (ids: string[]) => {
    console.log("Selection changed:", ids);
    setSelectedIds(ids);
  };

  // Cross-grid drag and drop handlers
  const handleExternalDropToSelected = useCallback(
    (
      rows: TreeGridRow[],
      sourceGridId: string,
      targetRowId: string | null,
      position: "before" | "after" | "child"
    ) => {
      console.log("External drop to selected grid:", {
        rows,
        sourceGridId,
        targetRowId,
        position,
      });

      // Create new rows with unique IDs for the target grid
      const newRows = rows.map((row, index) => ({
        ...row,
        id: `selected-${Date.now()}-${index}`,
        parentId: position === "child" ? targetRowId : null,
        depth: position === "child" ? 1 : 0,
        childrenIds: [],
      }));

      setSelectedProductsData((prev) => [...prev, ...newRows]);

      // Remove from source grid if it's the available products grid
      if (sourceGridId === "available-products") {
        const rowIds = rows.map((r) => r.id);
        setAvailableProductsData((prev) =>
          prev.filter((r) => !rowIds.includes(r.id))
        );
      }

      ToastQueue.positive(`Added ${rows.length} item(s) to Selected Products`, {
        timeout: 3000,
      });
    },
    []
  );

  const handleExternalDropToAvailable = useCallback(
    (
      rows: TreeGridRow[],
      sourceGridId: string,
      targetRowId: string | null,
      position: "before" | "after" | "child"
    ) => {
      console.log("External drop to available grid:", {
        rows,
        sourceGridId,
        targetRowId,
        position,
      });

      // Create new rows with unique IDs for the target grid
      const newRows = rows.map((row, index) => ({
        ...row,
        id: `avail-${Date.now()}-${index}`,
        parentId: null,
        depth: 0,
        childrenIds: [],
      }));

      setAvailableProductsData((prev) => [...prev, ...newRows]);

      // Remove from source grid if it's the selected products grid
      if (sourceGridId === "selected-products") {
        const rowIds = rows.map((r) => r.id);
        setSelectedProductsData((prev) =>
          prev.filter((r) => !rowIds.includes(r.id))
        );
      }

      ToastQueue.positive(
        `Returned ${rows.length} item(s) to Available Products`,
        { timeout: 3000 }
      );
    },
    []
  );

  const handleRowDraggedOut = useCallback(
    (rowIds: string[], targetGridId: string) => {
      console.log("Rows dragged out:", { rowIds, targetGridId });
    },
    []
  );

  const currentData =
    activeTab === "project-items"
      ? projectItemsData
      : activeTab === "transactions"
      ? transactionData
      : [];
  const currentColumns =
    activeTab === "project-items" ? projectItemsColumns : transactionColumns;

  //console.log('currentData',currentData);

  return (
    <Provider theme={defaultTheme}>
      <div
        style={{
          padding: "24px",
          maxWidth: "1400px",
          margin: "0 auto",
          position: "relative",
        }}
      >
        {isLoading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                background: "white",
                padding: "24px 48px",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #3498db",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              <div style={{ fontSize: "16px", fontWeight: 500 }}>Saving...</div>
            </div>
          </div>
        )}
        <style>{`@keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }`}</style>
        <h1 style={{ marginBottom: "24px" }}>TreeGrid Component Example</h1>

        {/* Info Panel */}
        <div
          style={{
            background: "#f5f5f5",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            border: "1px solid #ddd",
          }}
        >
          <h3 style={{ marginTop: 0 }}>Keyboard Shortcuts</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "12px",
              fontSize: "14px",
            }}
          >
            {/* <div><strong>↑↓←→</strong> - Navigate cells</div> */}
            <div>
              <strong>Tab</strong> - Next cell
            </div>
            <div>
              <strong>Enter / F2</strong> - Start editing
            </div>
            <div>
              <strong>Escape</strong> - Cancel editing
            </div>
            <div>
              <strong>Space</strong> - Toggle selection
            </div>
            <div>
              <strong>Ctrl/Cmd + Enter</strong> - Add new row
            </div>
            <div>
              <strong>Cmd/Ctrl + ]</strong> - Indent row
            </div>
            <div>
              <strong>Cmd/Ctrl + [</strong> - Outdent row
            </div>
          </div>
          <div style={{ marginTop: "12px", fontSize: "13px", color: "#666" }}>
            <strong>Selected rows:</strong>{" "}
            {selectedIds.length > 0 ? selectedIds.join(", ") : "None"}
          </div>
        </div>

        {/* ComboBox Info Panel - Only show for Project Items tab */}
        {activeTab === "project-items" && (
          <div
            style={{
              background: "#e3f2fd",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
              border: "1px solid #2196f3",
            }}
          >
            <h4 style={{ marginTop: 0, color: "#1565c0" }}>
              🔍 Product Search (ComboBox Demo)
            </h4>
            <p style={{ margin: "8px 0", fontSize: "14px", color: "#0d47a1" }}>
              <strong>Double-click</strong> on the <strong>Product Name</strong>{" "}
              column to open the product search. When you select a product, it
              will automatically populate:
            </p>
            <ul
              style={{
                margin: "8px 0",
                fontSize: "13px",
                color: "#1565c0",
                paddingLeft: "20px",
              }}
            >
              <li>
                <strong>SKU</strong> - Product identifier
              </li>
              <li>
                <strong>Unit Price</strong> - Current selling price
              </li>
              <li>
                <strong>Catalog Price</strong> - Original list price
              </li>
              <li>
                <strong>Total</strong> - Calculated from quantity × unit price
              </li>
            </ul>
            <p
              style={{ margin: "8px 0 0", fontSize: "12px", color: "#1976d2" }}
            >
              Try searching: "CPU", "Memory", "Monitor", "Network", or any
              category name
            </p>
          </div>
        )}

        {/* Tab Navigation */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
          <button
            onClick={() => setActiveTab("project-items")}
            style={{
              padding: "12px 24px",
              border: "1px solid #ddd",
              background: activeTab === "project-items" ? "#007bff" : "white",
              color: activeTab === "project-items" ? "white" : "black",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Project Items (Deal Items)
          </button>
          <button
            onClick={() => setActiveTab("transactions")}
            style={{
              padding: "12px 24px",
              border: "1px solid #ddd",
              background: activeTab === "transactions" ? "#007bff" : "white",
              color: activeTab === "transactions" ? "white" : "black",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Transactions (Invoice/Order/Offer)
          </button>
          <button
            onClick={() => setActiveTab("cross-grid")}
            style={{
              padding: "12px 24px",
              border: "1px solid #ddd",
              background: activeTab === "cross-grid" ? "#28a745" : "white",
              color: activeTab === "cross-grid" ? "white" : "black",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            🔄 Cross-Grid Drag & Drop
          </button>
        </div>

        {/* Cross-Grid Demo */}
        {activeTab === "cross-grid" && (
          <>
            <div
              style={{
                background: "#e8f5e9",
                padding: "16px",
                borderRadius: "8px",
                marginBottom: "16px",
                border: "1px solid #4caf50",
              }}
            >
              <h3 style={{ marginTop: 0, color: "#2e7d32" }}>
                Cross-Grid Drag & Drop Demo
              </h3>
              <p
                style={{ margin: "8px 0", fontSize: "14px", color: "#1b5e20" }}
              >
                <strong>Instructions:</strong> Drag items from the "Available
                Products" grid on the left to the "Selected Products" grid on
                the right. You can also drag items back to return them.
              </p>
              <ul
                style={{
                  margin: "8px 0",
                  fontSize: "13px",
                  color: "#2e7d32",
                  paddingLeft: "20px",
                }}
              >
                <li>
                  Use the drag handle (⋮⋮) on the left of each row to drag
                </li>
                <li>Drop on an empty grid or on a specific row</li>
                <li>Multiple selected items can be dragged together</li>
                <li>Items are automatically removed from the source grid</li>
              </ul>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "24px",
              }}
            >
              {/* Available Products Grid */}
              <div
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <TreeGridComponent
                  title="Available Products"
                  gridId="available-products"
                  columns={projectItemsColumns}
                  data={availableProductsData}
                  showSelection
                  showSelectAll
                  enableDragDrop
                  enableKeyboardNav
                  enableInlineEdit
                  pageSize={20}
                  indentPerLevel={32}
                  maxDepth={3}
                  showGridLines
                  showHover
                  showToolbar={false}
                  showInfoIcon={false}
                  showDeleteIcon={false}
                  acceptExternalDrop
                  acceptDropFromGridIds={["selected-products"]}
                  onExternalDrop={handleExternalDropToAvailable}
                  onRowDraggedOut={handleRowDraggedOut}
                  onSelectionChange={(ids) =>
                    console.log("Available selection:", ids)
                  }
                  emptyMessage="All products have been selected!"
                />
              </div>

              {/* Selected Products Grid */}
              <div
                style={{
                  border: "1px solid #2196f3",
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow: "0 0 0 2px rgba(33, 150, 243, 0.2)",
                }}
              >
                <TreeGridComponent
                  title="Selected Products"
                  gridId="selected-products"
                  columns={projectItemsColumns}
                  data={selectedProductsData}
                  showSelection
                  showSelectAll
                  enableDragDrop
                  enableKeyboardNav
                  enableInlineEdit
                  pageSize={20}
                  indentPerLevel={32}
                  maxDepth={3}
                  showGridLines
                  showHover
                  showToolbar={false}
                  showInfoIcon={false}
                  showDeleteIcon
                  acceptExternalDrop
                  acceptDropFromGridIds={["available-products"]}
                  onExternalDrop={handleExternalDropToSelected}
                  onRowDraggedOut={handleRowDraggedOut}
                  onDeleteClick={(rowId, row) => {
                    // Move back to available when deleted
                    const newRow = {
                      ...row,
                      id: `avail-${Date.now()}`,
                      parentId: null,
                      depth: 0,
                      childrenIds: [],
                    };
                    setAvailableProductsData((prev) => [...prev, newRow]);
                    setSelectedProductsData((prev) =>
                      prev.filter((r) => r.id !== rowId)
                    );
                    ToastQueue.info("Item returned to Available Products", {
                      timeout: 3000,
                    });
                  }}
                  onSelectionChange={(ids) =>
                    console.log("Selected selection:", ids)
                  }
                  emptyMessage="Drag products here to select them"
                />
              </div>
            </div>
          </>
        )}

        {/* Standard TreeGrid (Project Items / Transactions) */}
        {activeTab !== "cross-grid" && (
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <TreeGridComponent
              title={
                activeTab === "project-items" ? "Project Items" : "Transactions"
              }
              columns={currentColumns}
              data={currentData}
              showSelection
              showSelectAll
              enableDragDrop
              enableKeyboardNav
              enableInlineEdit
              pageSize={20}
              indentPerLevel={32}
              maxDepth={3}
              showGridLines
              showHover
              onRowUpdate={handleRowUpdate}
              onRowCreate={handleRowCreate}
              onRowDelete={handleRowDelete}
              onRowMove={handleRowMove}
              onSelectionChange={handleSelectionChange}
              onCellEdit={(rowId: string, columnKey: string, value: any) => {
                console.log("Cell edited:", { rowId, columnKey, value });
              }}
              onCellValidationError={(
                rowId: string,
                columnKey: string,
                error: string | null
              ) => {
                console.log("Validation error:", { rowId, columnKey, error });
              }}
              footerActions={
                [
                  {
                    key: "create-estimate",
                    label: (count) => `Create Estimate (${count})`,
                    onClick: () =>
                      ToastQueue.positive("Create Estimate clicked", {
                        timeout: 3000,
                      }),
                  },
                  {
                    key: "create-sales-order",
                    label: (count) => `Create Sales Order (${count})`,
                    onClick: () =>
                      ToastQueue.positive("Create Sales Order clicked", {
                        timeout: 3000,
                      }),
                  },
                  {
                    key: "create-invoice",
                    label: (count) => `Create Invoice (${count})`,
                    onClick: () =>
                      ToastQueue.positive("Create Invoice clicked", {
                        timeout: 3000,
                      }),
                  },
                ] as FooterAction[]
              }
            />
          </div>
        )}

        {/* Feature List */}
        <div
          style={{
            marginTop: "32px",
            padding: "16px",
            background: "#f9f9f9",
            borderRadius: "8px",
          }}
        >
          <h3>Features Demonstrated</h3>
          <ul style={{ columnCount: 2, columnGap: "32px" }}>
            <li>✅ Two-level hierarchy with expand/collapse</li>
            <li>✅ Inline editing (double-click or F2)</li>
            <li>
              ✅{" "}
              <strong style={{ color: "#2196f3" }}>
                ComboBox with async search (NEW!)
              </strong>
            </li>
            <li>
              ✅{" "}
              <strong style={{ color: "#2196f3" }}>
                Multi-field auto-populate on selection
              </strong>
            </li>
            <li>✅ Real-time validation with error icons</li>
            <li>✅ Checkbox selection with cascade</li>
            <li>✅ Select all functionality</li>
            <li>✅ Keyboard navigation (arrows, tab)</li>
            <li>✅ Drag & drop reordering (enabled)</li>
            <li>✅ Cross-grid drag & drop</li>
            <li>✅ Indent/outdent operations</li>
            <li>✅ Number formatting (decimals, currency)</li>
            <li>✅ Calculated fields (Total column)</li>
            <li>✅ Custom cell rendering</li>
            <li>✅ Pagination support</li>
            <li>✅ Create/delete rows</li>
            <li>✅ Responsive design</li>
            <li>✅ Accessibility (ARIA labels)</li>
          </ul>
        </div>
      </div>
    </Provider>
  );
}
